import { useCallback, useEffect, useRef, useState } from "react";
import { openTapeStream } from "../api/stream.js";
import type { TickerTapeSnapshot } from "../types.js";

/** `paused` = tab hidden, stream closed (chart kept). `displaced` = this account opened DeltaTape on another screen; waits for the user. */
export type TapeConnection = "connecting" | "open" | "error" | "displaced" | "paused";

export interface TapeStreamState {
  snapshot: TickerTapeSnapshot | null;
  connection: TapeConnection;
  /** Wall-clock ms of the last pane update (audit timestamp — Date.now() is fine). */
  lastUpdateMs: number | null;
}

const INITIAL: TapeStreamState = { snapshot: null, connection: "connecting", lastUpdateMs: null };

/** Subscribe to the DeltaTape SSE stream for a symbol; re-subscribes on change. */
export function useTapeStream(
  token: string | null,
  symbol: string,
): TapeStreamState & { reconnect: () => void } {
  const [state, setState] = useState<TapeStreamState>(INITIAL);
  // Bumped by the "Use DeltaTape here" button to reopen after being displaced.
  const [attempt, setAttempt] = useState(0);
  // Once displaced, ONLY "Use DeltaTape here" (reconnect) may take the screen
  // back. Opening a stream for any other reason (ticker or token change) would
  // send this page's screen id and silently bump the other device.
  const displacedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setState(INITIAL);
      return;
    }
    if (displacedRef.current) {
      setState((s) => ({ ...s, connection: "displaced" }));
      return;
    }
    setState(INITIAL);
    let lastSeq = 0;
    // True once THIS connection has delivered a full frame (deltas need a base).
    let haveFull = false;
    let close: (() => void) | null = null;

    const open = (resume = false): void => {
      lastSeq = 0;
      haveFull = false;
      close = openTapeStream(token, symbol, {
        onReady: () => setState((s) => ({ ...s, connection: "open" })),
        onPane: (snapshot) => {
          lastSeq = snapshot.seq ?? 0;
          haveFull = true;
          setState((s) => ({ ...s, snapshot, connection: "open", lastUpdateMs: Date.now() }));
        },
        onDelta: (d) => {
          if (!haveFull || d.seq !== lastSeq + 1) {
            // Missed a push (or no base yet): reopen for a fresh full frame.
            // The snapshot is NOT cleared, so the chart doesn't flash.
            resync();
            return;
          }
          lastSeq = d.seq;
          setState((s) =>
            s.snapshot
              ? {
                  ...s,
                  snapshot: {
                    ...s.snapshot,
                    pane: d.pane,
                    freshness: d.freshness,
                    line: [...s.snapshot.line, ...d.points],
                  },
                  connection: "open",
                  lastUpdateMs: Date.now(),
                }
              : s,
          );
        },
        onError: () =>
          setState((s) => (s.connection === "displaced" ? s : { ...s, connection: "error" })),
        onDisplaced: () => {
          displacedRef.current = true;
          close = null; // the stream already closed itself
          setState((s) => ({ ...s, connection: "displaced" }));
        },
      }, { resume });
    };
    const resync = (): void => {
      close?.();
      open();
    };

    // Hidden tabs hold no stream (saves bandwidth); the chart stays as it was.
    const onVisibility = (): void => {
      if (document.hidden) {
        close?.();
        close = null;
        setState((s) => (s.connection === "displaced" ? s : { ...s, connection: "paused" }));
      } else if (!displacedRef.current && close === null) {
        setState((s) => ({ ...s, connection: "connecting" }));
        // Returning from hidden must not kick the member's other device.
        open(true);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    if (document.hidden) {
      setState((s) => ({ ...s, connection: "paused" }));
    } else {
      open();
    }
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      close?.();
    };
  }, [token, symbol, attempt]);

  const reconnect = useCallback(() => {
    displacedRef.current = false;
    setAttempt((n) => n + 1);
  }, []);
  return { ...state, reconnect };
}
